import { Injectable, inject } from '@angular/core';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { environment } from '@env/environment';
import { AuthService } from './auth.service';

/**
 * Application Insights service for the SWA (Angular) side.
 *
 * - Initialises the App Insights SDK on startup.
 * - Sets the authenticated user context (email) as soon as the user resolves,
 *   so every telemetry item is tagged with the user who triggered it.
 * - Exposes helpers used by the HTTP error interceptor and components.
 *
 * Connection string: set `appInsightsConnectionString` in the relevant
 * src/environments/environment.*.ts file.
 * Get the value from: Azure Portal ➜ Application Insights ➜ Overview ➜ Connection String.
 */
@Injectable({ providedIn: 'root' })
export class AppInsightsService {
  private appInsights: ApplicationInsights | undefined;
  private authService = inject(AuthService);

  constructor() {
    this.initialize();
  }

  // ── Initialisation ────────────────────────────────────────────────────────

  private initialize(): void {
    const connectionString = (environment as any).appInsightsConnectionString as string | undefined;
    if (!connectionString) {
      // No connection string configured — telemetry is silently disabled.
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          // Automatically track Angular route changes as page views
          enableAutoRouteTracking: true,
          // Propagate correlation headers to API calls for end-to-end tracing
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          // Don't fetch the dynamic config from the js.monitor.azure.com CDN —
          // avoids a CSP hole and an external dependency at startup.
          extensionConfig: {
            ['AppInsightsCfgSyncPlugin']: { cfgUrl: '' },
          },
        }
      });

      this.appInsights.loadAppInsights();
      this.appInsights.trackPageView();
    } catch (err) {
      console.warn('[AppInsights] Failed to initialise — telemetry disabled.', err);
      this.appInsights = undefined;
      return;
    }

    // Tag every telemetry item with the authenticated user's email
    this.authService.getCurrentUser$().subscribe(user => {
      if (user?.email) {
        // accountId (second arg) groups by user in the Users blade
        this.appInsights?.setAuthenticatedUserContext(
          user.email,   // authenticatedUserId — searchable in App Insights
          user.email,   // accountId
          true          // storeInCookie — survives page reloads
        );

        // Telemetry initializer: inject userEmail into customDimensions on
        // every telemetry item (page views, requests, traces, exceptions).
        // Equivalent to C# CustomTelemetryInitializer.
        // Query in Logs: requests | where customDimensions.userEmail == "user@chevron.com"
        this.appInsights?.addTelemetryInitializer(item => {
          item.data = item.data ?? {};
          item.data['userEmail'] = user.email;
        });
      }
    });
  }

  // ── Public telemetry helpers ──────────────────────────────────────────────

  /**
   * Track an exception. Used by the HTTP error interceptor to surface
   * API failures (e.g. 500s from the Function) in the Failures blade.
   */
  trackException(
    error: Error,
    properties?: Record<string, string>
  ): void {
    this.appInsights?.trackException({ exception: error, severityLevel: SeverityLevel.Error }, properties);
  }

  /** Track a named custom event (e.g. "NormsLookup", "TaxonomyDeployed"). */
  trackEvent(name: string, properties?: Record<string, string>): void {
    this.appInsights?.trackEvent({ name }, properties);
  }

  /** Emit a diagnostic trace message (visible in the Traces blade). */
  trackTrace(message: string, properties?: Record<string, string>): void {
    this.appInsights?.trackTrace({ message, severityLevel: SeverityLevel.Information }, properties);
  }
}
